// src/components/ResolvingLoader.jsx
// Blocking modal loader for geocoding/city resolution with rotating narrative tips
// and adaptive titles/status based on progress.

import React, { useState, useEffect, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';

// -------------------------------------------------------------
// Narrative, PR‑worthy rotating tips (ordered to feel cinematic)
// -------------------------------------------------------------
const TIPS = [
  "Your moments are awakening — each photo holds a piece of your journey.",
  "We’re organizing your memories into chapters — one for every city you explored.",
  "Each chapter tells a story. You can rename them, merge them, or make them your own.",
  "While reliving your journey, tap the ❤️ on any photo you love — it helps shape your Auto Album.",
  "You can delete a photo to keep your story focused on what truly matters.",
  "Soon, you’ll see your travels transformed into a visual timeline — a living map of your memories.",
  "Want to go further? You can edit captions, reorder pages, or create a printed album ready for your shelf.",
  "Every image, every place, every heartbeat — your story is about to come alive."
];

// -------------------------------------------------------------
// Adaptive titles & status lines based on progress
// -------------------------------------------------------------
function getTitleForProgress(pct) {
  if (pct < 20) return 'Resurrecting memories…';
  if (pct < 40) return 'Tracing your footsteps…';
  if (pct < 60) return 'Organizing your chapters…';
  if (pct < 80) return 'Curating your favorites…';
  if (pct < 100) return 'Your story is almost ready…';
  return 'All set ✨';
}

function getStatusForProgress(pct) {
  if (pct < 10) return 'Scanning photos and reading metadata…';
  if (pct < 25) return 'Clustering moments by proximity…';
  if (pct < 40) return 'Resolving city and place names…';
  if (pct < 55) return 'Sequencing your timeline…';
  if (pct < 70) return 'Picking likely highlights (you can ❤️ to bias Auto Album)…';
  if (pct < 85) return 'Building chapters and step pages…';
  if (pct < 100) return 'Polishing layout and preparing preview…';
  return 'Ready to explore';
}

export default function ResolvingLoader({
  isVisible,
  statusMessage, // optional: overrides adaptive status
  progress = 0, // 0–100
  onCancel,
  cancelLabel = 'Cancel',
  onComplete
}) {
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);

  // Rotate tips every 5 seconds (with quick fade)
  useEffect(() => {
    if (!isVisible) return;

    const tipInterval = setInterval(() => {
      setIsFading(true);
      setTimeout(() => {
        setCurrentTipIndex(prev => (prev + 1) % TIPS.length);
        setIsFading(false);
      }, 300);
    }, 5000);

    return () => clearInterval(tipInterval);
  }, [isVisible]);

  // Call onComplete when progress reaches 100
  useEffect(() => {
    if (progress >= 100 && onComplete) {
      const timer = setTimeout(() => onComplete(), 1500); // brief victory lap
      return () => clearTimeout(timer);
    }
  }, [progress, onComplete]);

  const clamped = Math.max(0, Math.min(progress, 100));

  // Compute adaptive title / status (memoized for tiny perf wins)
  const computedTitle = useMemo(() => getTitleForProgress(clamped), [clamped]);
  const computedStatus = useMemo(
    () => statusMessage || getStatusForProgress(clamped),
    [statusMessage, clamped]
  );

  if (!isVisible) return null;

  return (
    <Overlay className={isVisible ? 'visible' : ''} aria-modal="true" role="dialog" aria-label="Resolving your journey">
      <LoaderCard>
        <LoaderIcon aria-hidden>🌍</LoaderIcon>
        <Title>{computedTitle}</Title>
        <Subtitle className={isFading ? 'fading' : ''}>Crafting your travel story…</Subtitle>

        <StatusText>{computedStatus}</StatusText>

        <ProgressBarOuter aria-label={`Progress: ${clamped}%`} role="progressbar" aria-valuenow={clamped} aria-valuemin={0} aria-valuemax={100}>
          <ProgressBarInner style={{ width: `${clamped}%` }} />
        </ProgressBarOuter>

        <TipsSection>
          <TipsLabel>💡 Did you know?</TipsLabel>
          <TipText className={isFading ? 'fading' : ''}>
            {TIPS[currentTipIndex]}
          </TipText>
        </TipsSection>

        {typeof onCancel === 'function' ? (
          <CancelRow>
            <CancelButton type="button" onClick={onCancel}>
              {cancelLabel}
            </CancelButton>
          </CancelRow>
        ) : null}
      </LoaderCard>
    </Overlay>
  );
}

// ===========================================
// STYLED COMPONENTS
// ===========================================

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;

  &.visible {
    opacity: 1;
    visibility: visible;
  }
`;

const LoaderCard = styled.div`
  background-color: white;
  border-radius: 20px;
  padding: 40px;
  width: 90%;
  max-width: 520px;
  text-align: center;
  box-shadow: 0 20px 60px -10px rgba(0, 0, 0, 0.3);
  transform: scale(0.95);
  transition: transform 0.3s ease;

  ${Overlay}.visible & {
    transform: scale(1);
  }
`;

const LoaderIcon = styled.div`
  font-size: 40px;
  animation: ${spin} 1.5s linear infinite;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 800;
  color: #111827;
  margin-top: 16px;
  margin-bottom: 4px;
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 13px;
  margin-top: 0;
  margin-bottom: 12px;
  transition: opacity 0.3s ease-in-out;

  &.fading {
    opacity: 0.6;
  }
`;

const StatusText = styled.p`
  color: #374151;
  font-size: 14px;
  margin-top: 6px;
`;

const ProgressBarOuter = styled.div`
  width: 100%;
  background-color: #e5e7eb;
  border-radius: 99px;
  height: 12px;
  overflow: hidden;
  margin-top: 18px;
`;

const ProgressBarInner = styled.div`
  height: 100%;
  background-color: #3b82f6;
  border-radius: 99px;
  transition: width 0.5s ease-out;
`;

const TipsSection = styled.div`
  margin-top: 24px;
  text-align: left;
`;

const TipsLabel = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
`;

const CancelRow = styled.div`
  margin-top: 18px;
  display: flex;
  justify-content: center;
`;

const CancelButton = styled.button`
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  border-radius: 12px;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover { background: #f9fafb; }
  &:active { transform: translateY(1px); }
`;

const TipText = styled.p`
  color: #374151;
  font-size: 14px;
  line-height: 1.6;
  min-height: 40px;
  transition: opacity 0.3s ease-in-out;

  &.fading {
    opacity: 0;
  }
`;
